import { supabase } from '../../config/supabase';
import { Driver } from '../../types/cms.types';
import { CMSListResponse, CMSApiResponse } from '../../types/cms.types';

export class DriverService {
    private static instance: DriverService;

    public static getInstance(): DriverService {
        if (!DriverService.instance) {
            DriverService.instance = new DriverService();
        }
        return DriverService.instance;
    }

    // Get drivers with pagination and filters
    async getDrivers(
        page: number = 1,
        limit: number = 10,
        filters?: {
            status?: string;
            vehicle_type?: string;
            city_of_operation?: string;
            search?: string;
            is_online?: boolean;
        }
    ): Promise<CMSListResponse<Driver>> {
        try {
            let query = supabase
                .from('drivers')
                .select('*', { count: 'exact' });

            // Apply filters
            if (filters?.status && filters.status !== '') {
                query = query.eq('status', filters.status);
            }

            if (filters?.vehicle_type && filters.vehicle_type !== '') {
                query = query.eq('vehicle_type', filters.vehicle_type);
            }

            if (filters?.city_of_operation && filters.city_of_operation !== '') {
                query = query.eq('city_of_operation', filters.city_of_operation);
            }

            if (filters?.is_online !== undefined) {
                query = query.eq('is_online', filters.is_online);
            }

            // Search filter
            if (filters?.search && filters.search.trim() !== '') {
                const searchTerm = filters.search.trim();
                query = query.or(`full_name.ilike.%${searchTerm}%,phone_number.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,motorcycle_plate_number.ilike.%${searchTerm}%,car_plate_number.ilike.%${searchTerm}%`);
            }

            // Apply pagination
            const from = (page - 1) * limit;
            const to = from + limit - 1;

            query = query
                .order('created_at', { ascending: false })
                .range(from, to);

            const { data, count, error } = await query;

            if (error) {
                console.error('Error fetching drivers:', error);
                return {
                    data: [],
                    pagination: { page, limit, total: 0, totalPages: 0 },
                    error: error.message,
                    success: false
                };
            }

            const total = count || 0;
            const totalPages = Math.ceil(total / limit);

            return {
                data: data || [],
                pagination: { page, limit, total, totalPages },
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in getDrivers:', error);
            return {
                data: [],
                pagination: { page, limit, total: 0, totalPages: 0 },
                error: error.message || 'Có lỗi xảy ra khi tải danh sách tài xế',
                success: false
            };
        }
    }

    // Get driver by ID
    async getDriverById(id: string): Promise<CMSApiResponse<Driver>> {
        try {
            const { data, error } = await supabase
                .from('drivers')
                .select('*')
                .eq('id', id)
                .single();

            if (error) {
                console.error('Error fetching driver:', error);
                return {
                    data: null,
                    error: error.message,
                    success: false
                };
            }

            return {
                data,
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in getDriverById:', error);
            return {
                data: null,
                error: error.message || 'Có lỗi xảy ra khi tải thông tin tài xế',
                success: false
            };
        }
    }

    // Create driver
    async createDriver(driver: Omit<Driver, 'id' | 'created_at' | 'updated_at'>): Promise<CMSApiResponse<Driver>> {
        try {
            const { data, error } = await supabase
                .from('drivers')
                .insert([driver])
                .select()
                .single();

            if (error) {
                console.error('Error creating driver:', error);
                return {
                    data: null,
                    error: error.message,
                    success: false
                };
            }

            return {
                data,
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in createDriver:', error);
            return {
                data: null,
                error: error.message || 'Có lỗi xảy ra khi tạo tài xế',
                success: false
            };
        }
    }

    // Update driver
    async updateDriver(id: string, updates: Partial<Driver>): Promise<CMSApiResponse<Driver>> {
        try {
            const { data, error } = await supabase
                .from('drivers')
                .update({ ...updates, updated_at: new Date().toISOString() })
                .eq('id', id)
                .select()
                .single();

            if (error) {
                console.error('Error updating driver:', error);
                return {
                    data: null,
                    error: error.message,
                    success: false
                };
            }

            return {
                data,
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in updateDriver:', error);
            return {
                data: null,
                error: error.message || 'Có lỗi xảy ra khi cập nhật tài xế',
                success: false
            };
        }
    }

    // Delete driver
    async deleteDriver(id: string): Promise<CMSApiResponse<boolean>> {
        try {
            const { error } = await supabase
                .from('drivers')
                .delete()
                .eq('id', id);

            if (error) {
                console.error('Error deleting driver:', error);
                return {
                    data: false,
                    error: error.message,
                    success: false
                };
            }

            return {
                data: true,
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in deleteDriver:', error);
            return {
                data: false,
                error: error.message || 'Có lỗi xảy ra khi xóa tài xế',
                success: false
            };
        }
    }

    // Update driver status (approve/reject)
    async updateDriverStatus(id: string, status: 'approved' | 'rejected' | 'pending'): Promise<CMSApiResponse<Driver>> {
        try {
            const updates: Partial<Driver> = {
                status,
                updated_at: new Date().toISOString()
            };

            // If approving, set activation date
            if (status === 'approved') {
                updates.activation_date = new Date().toISOString();
            }

            const { data, error } = await supabase
                .from('drivers')
                .update(updates)
                .eq('id', id)
                .select()
                .single();

            if (error) {
                console.error('Error updating driver status:', error);
                return {
                    data: null,
                    error: error.message,
                    success: false
                };
            }

            return {
                data,
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in updateDriverStatus:', error);
            return {
                data: null,
                error: error.message || 'Có lỗi xảy ra khi cập nhật trạng thái tài xế',
                success: false
            };
        }
    }

    // Get driver statistics
    async getDriverStats(): Promise<CMSApiResponse<{
        total: number;
        pending: number;
        approved: number;
        rejected: number;
        online: number;
        car_drivers: number;
        motorbike_drivers: number;
    }>> {
        try {
            const { data: allDrivers, error } = await supabase
                .from('drivers')
                .select('status, vehicle_type, is_online');

            if (error) {
                console.error('Error fetching driver stats:', error);
                return {
                    data: null,
                    error: error.message,
                    success: false
                };
            }

            const stats = {
                total: allDrivers?.length || 0,
                pending: allDrivers?.filter((d: any) => d.status === 'pending').length || 0,
                approved: allDrivers?.filter((d: any) => d.status === 'approved').length || 0,
                rejected: allDrivers?.filter((d: any) => d.status === 'rejected').length || 0,
                online: allDrivers?.filter((d: any) => d.is_online === true).length || 0,
                car_drivers: allDrivers?.filter((d: any) => d.vehicle_type === 'car').length || 0,
                motorbike_drivers: allDrivers?.filter((d: any) => d.vehicle_type === 'motorbike').length || 0,
            };

            return {
                data: stats,
                error: null,
                success: true
            };
        } catch (error: any) {
            console.error('Error in getDriverStats:', error);
            return {
                data: null,
                error: error.message || 'Có lỗi xảy ra khi tải thống kê tài xế',
                success: false
            };
        }
    }
}
